import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, Lock, Eye, FileCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { GridBackground } from '@/components/GridBackground';
import { MainNavigation } from '@/components/MainNavigation';
import { Footer } from '@/components/Footer';

const securityFeatures = [
  {
    icon: Shield,
    title: 'Advanced Threat Protection',
    description: 'Multi-layered security system protecting against phishing, malware, and unauthorized access attempts.'
  },
  {
    icon: Lock,
    title: 'End-to-End Encryption',
    description: 'All data transmitted through our gateways is encrypted using industry-standard SSL/TLS protocols.'
  },
  {
    icon: Eye,
    title: 'Privacy Guaranteed',
    description: 'We never sell or share your data. Your privacy is protected by our strict no-logging policy.'
  },
  {
    icon: FileCheck,
    title: 'Regular Security Audits',
    description: 'Our systems undergo quarterly security audits by independent third-party experts.'
  }
];

export const SecurityPage = () => {
  return (
    <div className="min-h-screen relative">
      <GridBackground />
      <MainNavigation />

      <main className="py-20 px-4">
        <div className="max-w-4xl mx-auto">
          <Button asChild variant="outline" className="mb-8 glass border-primary/30">
            <Link to="/">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Link>
          </Button>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="glass rounded-2xl p-8 md:p-12 space-y-8"
          >
            <h1 className="text-4xl font-bold">Security & Trust</h1>

            <p className="text-muted-foreground">
              At XtreamCube, security is not just a feature—it's the foundation of everything we do.
              We employ industry-leading security measures to ensure your data and connections remain safe.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-6">
              {securityFeatures.map((feature, index) => (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="space-y-3 p-6 rounded-lg bg-card/50 border border-border"
                >
                  <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                    <feature.icon className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="text-lg font-semibold">{feature.title}</h3>
                  <p className="text-sm text-muted-foreground">
                    {feature.description}
                  </p>
                </motion.div>
              ))}
            </div>

            <div className="pt-6 border-t border-border space-y-4">
              <h2 className="text-2xl font-semibold">Our Security Commitment</h2>
              <ul className="space-y-2 text-muted-foreground">
                <li>• 256-bit SSL encryption on all connections</li>
                <li>• Two-factor authentication (2FA) support</li>
                <li>• Real-time threat monitoring and response</li>
                <li>• Compliance with GDPR and international data protection standards</li>
                <li>• Regular penetration testing and vulnerability assessments</li>
              </ul>


            </div>
          </motion.div>
        </div>
      </main>

      <Footer />
    </div>
  );
};